import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SessionService } from 'app/core/auth/session.service';
import { environment } from 'environments/environment';
import { COOKIE_SESSION_REQUEST } from 'app/core/auth/session.types';
import { BehaviorSubject, catchError, distinctUntilChanged, exhaustMap, filter, fromEvent, map, merge, of, startWith, switchMap, timer, timeout } from 'rxjs';

/** Otro usuario de la misma empresa con un latido reciente en SES. */
export interface UsuarioEnLinea {
    kusr: number;
    name: string;
    tipo: string;
    /** De su sesión con el latido más reciente. */
    ipad: string | null;
    uagt: string | null;
    /** Inicio de esa sesión (ISO, UTC). */
    desde: string | null;
    /** Segundos conectado según el reloj de Oracle al responder. */
    segs: number;
    /** Solo en la vista global del superadmin. */
    kemp?: number;
    empresa?: string;
}

/**
 * Un único ciclo por aplicación, independiente del layout y del tipo de usuario.
 * POST /api/presence/heartbeat registra la sesión y devuelve los usuarios visibles.
 * La identidad procede exclusivamente de la cookie; SES debe compartirse entre backends.
 */
@Injectable({
    providedIn: 'root'
})
export class PresenceService {

    private started = false;
    private readonly state = new BehaviorSubject<{ users: UsuarioEnLinea[]; error: boolean; errorMessage: string }>({ users: [], error: false, errorMessage: '' });
    readonly state$ = this.state.asObservable();

    constructor(private http: HttpClient, private session: SessionService, private destroyRef: DestroyRef) { }

    start(): void {
        if (this.started) return;
        this.started = true;
        this.session.state$.pipe(
            map(state => state.status === 'authenticated' ? state.context : null),
            distinctUntilChanged((a, b) => a?.user.id === b?.user.id && a?.company.id === b?.company.id && a?.user.tipo === b?.user.tipo),
            switchMap(context => {
                const empty = { users: [] as UsuarioEnLinea[], error: false, errorMessage: '' };
                if (!context) return of(empty);
                // RAM/CEO ven su empresa; el superadmin (admin@amzprp.cl, empresa 1), todas.
                const superAdmin = Number(context.company.id) === 1
                    && String(context.user.email ?? '').trim().toLowerCase() === 'admin@amzprp.cl';
                const canSee = superAdmin || ['RAM', 'CEO', 'DOM'].includes(String(context.user.tipo).trim().toUpperCase());
                return merge(
                    timer(0, 30_000),
                    fromEvent(window, 'online'),
                    fromEvent(window, 'focus'),
                    fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'visible')),
                ).pipe(
                    // Una petición lenta no acumula ni cancela latidos en curso.
                    exhaustMap(() => this.heartbeat().pipe(
                        map(users => ({ users: canSee ? users : [], error: false, errorMessage: '' })),
                        catchError(error => of({ users: [], error: canSee, errorMessage: canSee ? this.describeError(error) : '' })),
                    )),
                    startWith(empty),
                );
            }),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(state => this.state.next(state));
    }

    private describeError(error: unknown): string {
        // Solo códigos y textos propios: nunca cuerpo HTTP, cookies ni cabeceras.
        if (error instanceof HttpErrorResponse) {
            switch (error.status) {
                case 401:
                    return 'La sesión ya no es válida (401). Vuelve a iniciar sesión. Para usuarios simultáneos, usa perfiles o navegadores distintos.';
                case 403:
                    return 'El servidor rechazó el acceso a presencia (403).';
                case 404:
                    return 'El servidor no tiene disponible la ruta de presencia (404). Comprueba la versión del backend.';
                case 0:
                    return 'No se pudo conectar con el servidor de presencia. Reintentando…';
                default:
                    return `No se pudo consultar usuarios en línea (HTTP ${error.status}). Reintentando…`;
            }
        }
        return 'No se pudo consultar usuarios en línea: tiempo de espera agotado o error de respuesta. Reintentando…';
    }

    private heartbeat() {
        return this.http.post<UsuarioEnLinea[]>(`${environment.urlApi.replace(/\/$/, '')}/api/presence/heartbeat`, {}, {
            withCredentials: true,
            context: new HttpContext().set(COOKIE_SESSION_REQUEST, true),
        }).pipe(timeout(10000));
    }
}
